import { DocumentModel, ParagraphBlock, Token } from './types';
import { classifyToken, isPunctuationOrSymbol, normalizeForMatching } from './normalizer';
import { calculateTokenWeights } from './weights';

const LINE_SPLIT_REGEX = /\r\n|\r|\n|\u2028|\u2029/g;

/**
 * Builds the immutable DocumentModel from raw string input.
 * Strictly preserves the user's original code-point sequence.
 */
export function createDocumentModel(rawText: string): DocumentModel {
  // 1. Identify paragraph blocks
  const blocks: ParagraphBlock[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  LINE_SPLIT_REGEX.lastIndex = 0;
  let blockIndex = 0;

  while ((match = LINE_SPLIT_REGEX.exec(rawText)) !== null) {
    const text = rawText.slice(lastIndex, match.index);
    blocks.push({
      index: blockIndex++,
      start: lastIndex,
      end: match.index,
      text,
      isBlank: text.trim().length === 0,
    });
    lastIndex = LINE_SPLIT_REGEX.lastIndex;
  }

  // Final block
  const tailText = rawText.slice(lastIndex);
  blocks.push({
    index: blockIndex++,
    start: lastIndex,
    end: rawText.length,
    text: tailText,
    isBlank: tailText.trim().length === 0,
  });

  // 2. Tokenize blocks while maintaining source offsets
  interface RawTokenCandidate {
    start: number;
    end: number;
    block: number;
    raw: string;
  }

  const rawCandidates: RawTokenCandidate[] = [];

  for (const block of blocks) {
    const blockText = block.text;
    const blockStart = block.start;
    const len = blockText.length;
    let i = 0;

    while (i < len) {
      const ch = blockText[i]!;

      // Skip whitespace
      if (/\s/.test(ch)) {
        i++;
        continue;
      }

      // Check if standalone punctuation
      if (isPunctuationOrSymbol(ch)) {
        i++;
        continue;
      }

      // Accumulate word characters
      const tokenStart = i;
      while (i < len) {
        const c = blockText[i]!;
        if (/\s/.test(c) || isPunctuationOrSymbol(c)) {
          break;
        }
        i++;
      }
      const tokenEnd = i;

      const raw = blockText.slice(tokenStart, tokenEnd);
      rawCandidates.push({
        start: blockStart + tokenStart,
        end: blockStart + tokenEnd,
        block: block.index,
        raw,
      });
    }
  }

  // 3. Normalize candidates and classify
  const intermediateTokens = rawCandidates.map((c) => {
    const norm = normalizeForMatching(c.raw);
    const kind = classifyToken(c.raw);
    return {
      ...c,
      norm,
      kind,
    };
  });

  // Filter out any candidates that normalized to empty
  const validCandidates = intermediateTokens.filter((t) => t.norm.length > 0);

  // 4. Compute weights across all valid tokens
  const weights = calculateTokenWeights(validCandidates);

  // 5. Finalize tokens array
  const tokens: Token[] = validCandidates.map((t, idx) => ({
    index: idx,
    start: t.start,
    end: t.end,
    block: t.block,
    norm: t.norm,
    weight: weights[idx] ?? 1.0,
    kind: t.kind,
  }));

  return {
    rawText,
    blocks,
    tokens,

    tokenAtOffset(offset: number): Token | null {
      if (tokens.length === 0 || offset < 0 || offset > rawText.length) {
        return null;
      }

      // Binary search for token enclosing or nearest to offset
      let low = 0;
      let high = tokens.length - 1;

      while (low <= high) {
        const mid = (low + high) >> 1;
        const tok = tokens[mid]!;
        if (offset >= tok.start && offset <= tok.end) {
          return tok;
        }
        if (offset < tok.start) {
          high = mid - 1;
        } else {
          low = mid + 1;
        }
      }

      // If between tokens, return the closest previous token if reasonable
      if (high >= 0 && high < tokens.length) {
        return tokens[high]!;
      }
      if (low >= 0 && low < tokens.length) {
        return tokens[low]!;
      }

      return null;
    },

    tokensInRange(startOffset: number, endOffset: number): readonly Token[] {
      if (tokens.length === 0 || startOffset >= endOffset) return [];
      return tokens.filter((t) => t.start < endOffset && t.end > startOffset);
    },

    rangeOfToken(tokenIndex: number): { start: number; end: number } | null {
      const tok = tokens[tokenIndex];
      if (!tok) return null;
      return { start: tok.start, end: tok.end };
    },
  };
}
